import { useDeveloperModeEnabled } from '@chipinside/devmode'
import { useFetch } from '@chipinside/fetcher'
import { useMemo } from 'react'

import {
  EntityKey,
  EntitySearchData,
  EntitySearchParams,
  EntitySearchResults,
} from '#/components/AdvancedSearch/types'
import { useFarmSlug } from '#/store/ducks/farm'
import { endpoint } from '#/store/services/endpoints'

/** Animais, lotes e coleiras da fazenda atual */
function useFarmEntitiesSearch({ isFarmContext, search }: EntitySearchParams) {
  const farmSlug = useFarmSlug()

  const enabled = Boolean(search) && isFarmContext

  const { data, isValidating } = useFetch<EntitySearchData>({
    controller: enabled ? endpoint.farm.global_search({ farm: farmSlug }) : null,
    params: { search },
    swrOptions: { keepPreviousData: true },
  })

  return { data: enabled ? data : undefined, isValidating }
}

/** Fazendas às quais o usuário tem acesso */
function useFarmsSearch({ devMode, isFarmContext, search }: EntitySearchParams) {
  const enabled = Boolean(search) && (!isFarmContext || devMode)

  const { data, isValidating } = useFetch<EntitySearchResults>({
    controller: enabled ? endpoint.farm.search() : null,
    params: { search },
    swrOptions: { keepPreviousData: true },
  })

  return { data: enabled ? { farms: data } : undefined, isValidating }
}

/** Une as buscas de entidade num único conjunto de resultados */
export function useEntitySearch(search: string, isFarmContext: boolean) {
  const devMode = useDeveloperModeEnabled()
  const farmEntities = useFarmEntitiesSearch({ isFarmContext, search })
  const farms = useFarmsSearch({ devMode, isFarmContext, search })

  const data = useMemo<EntitySearchData>(
    () => ({ ...farmEntities.data, ...farms.data }),
    [farmEntities.data, farms.data],
  )

  return {
    data,
    isValidating: farmEntities.isValidating || farms.isValidating,
  }
}

export function getEntityCount(data: EntitySearchData | undefined, entity: EntityKey): number {
  const entityData = data?.[entity]
  return entityData?.total ?? entityData?.results?.length ?? 0
}
